/**
 * MessageReactions Component
 * Displays and manages reactions/likes on a message
 */

import React, { useState } from 'react';
import { Button, Popover, Tooltip } from 'antd';
import { SmileOutlined } from '@ant-design/icons';
import { useAuth } from '@/context/AuthContext';
import { useMessageLikes, useToggleLike } from '@/hooks/useChat';
import type { LikeType } from '@/api/types/chat.types';
import './MessageReactions.css';

interface MessageReactionsProps {
  /** Message ID */
  messageId: string;
  /** Show add reaction button */
  showAddButton?: boolean;
}

// Emoji mapping for like types
const REACTION_EMOJIS: Record<LikeType, string> = {
  thumbs_up: '👍',
  thumbs_down: '👎',
  heart: '❤️',
  laugh: '😂',
  surprised: '😮',
  sad: '😢',
  angry: '😠',
};

const REACTION_LABELS: Record<LikeType, string> = {
  thumbs_up: 'Thumbs Up',
  thumbs_down: 'Thumbs Down',
  heart: 'Heart',
  laugh: 'Laugh',
  surprised: 'Surprised',
  sad: 'Sad',
  angry: 'Angry',
};

export const MessageReactions: React.FC<MessageReactionsProps> = ({
  messageId,
  showAddButton = true,
}) => {
  const { user } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Don't fetch likes for temporary messages (they don't exist on backend yet)
  const isTempMessage = messageId?.startsWith('temp-') || false;

  // Fetch likes for this message (skip if it's a temp message)
  const { data: likes = [], isLoading } = useMessageLikes(messageId, {
    enabled: !isTempMessage,
  });

  // Toggle like mutation
  const toggleLike = useToggleLike();

  // Group likes by type and count
  const likesByType = likes.reduce(
    (acc, like) => {
      const type = like.like_type as LikeType;
      if (!acc[type]) {
        acc[type] = {
          count: 0,
          userNames: [],
          hasCurrentUser: false,
        };
      }
      acc[type].count++;
      acc[type].userNames.push(
        like.created_by_user?.display_name || 'Unknown User'
      );
      if (like.created_by === user?.id) {
        acc[type].hasCurrentUser = true;
      }
      return acc;
    },
    {} as Record<
      LikeType,
      { count: number; userNames: string[]; hasCurrentUser: boolean }
    >
  );

  // Handle reaction click
  const handleReactionClick = async (likeType: LikeType) => {
    const hasLike = likesByType[likeType]?.hasCurrentUser || false;

    try {
      await toggleLike.mutate({
        messageId,
        likeType,
        hasLike,
      });

      // Close picker after selecting
      setPickerOpen(false);
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
    }
  };

  // Reaction picker content
  const reactionPicker = (
    <div className="reaction-picker">
      {(Object.keys(REACTION_EMOJIS) as LikeType[]).map((type) => (
        <Tooltip key={type} title={REACTION_LABELS[type]}>
          <button
            className="reaction-picker-button"
            onClick={() => handleReactionClick(type)}
          >
            {REACTION_EMOJIS[type]}
          </button>
        </Tooltip>
      ))}
    </div>
  );

  return (
    <div className="message-reactions">
      {/* Existing reactions */}
      <div className="message-reactions-list">
        {Object.entries(likesByType).map(([type, data]) => (
          <Tooltip key={type} title={data.userNames.join(', ')}>
            <button
              className={`message-reaction-badge ${
                data.hasCurrentUser ? 'message-reaction-active' : ''
              }`}
              onClick={() => handleReactionClick(type as LikeType)}
              disabled={toggleLike.isLoading}
            >
              <span className="message-reaction-emoji">
                {REACTION_EMOJIS[type as LikeType]}
              </span>
              {data.count > 1 && (
                <span className="message-reaction-count">{data.count}</span>
              )}
            </button>
          </Tooltip>
        ))}
      </div>

      {/* Add reaction button */}
      {showAddButton && (
        <Popover
          content={reactionPicker}
          trigger="click"
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          placement="top"
        >
          <Button
            className="message-reactions-add"
            type="text"
            size="small"
            icon={<SmileOutlined />}
            loading={isLoading}
          />
        </Popover>
      )}
    </div>
  );
};
